import { useEffect, useMemo, useRef, useState } from 'react'
import { router, usePage, usePoll } from '@inertiajs/react'
import ProjectTaskCanvas from '@/frontend/features/Fde/ProjectTaskCanvas'
import ProjectWorkSummary from '@/frontend/features/Fde/ProjectWorkSummary'
import ProjectManagementToolbar from '@/frontend/features/Fde/ProjectManagementToolbar'
import {
  projectTaskMoveProjection,
  useProjectTaskActions,
} from '@/frontend/features/Fde/useProjectTaskActions'
import { fdeProjectPath, fdeRoutes } from '@/frontend/features/Fde/routes'
import type {
  ManagementProps,
  ProjectManagementView,
  ProjectTaskFilters,
} from '@/frontend/features/Fde/managementTypes'

const defaultFilters: ProjectTaskFilters = { query: '', status: 'all', assignee: '' }
const refreshProps = [
  'metrics',
  'tasks',
  'overdue_task_ids',
  'attention_task_ids',
  'management_today',
  'work_summary',
  'project_plan',
  'objectives',
  'task_categories',
]

export default function ProjectManagement({ props }: { props: ManagementProps }) {
  const { url } = usePage()
  const query = new URLSearchParams(url.split('?')[1] || '')
  const taskViews = ['board', 'list', 'schedule', 'gantt']
  const isSummary = props.management_section !== 'tasks'
  const actions = useProjectTaskActions(props.project?.id, props.copy)
  // Rails owns the records. Polling keeps agent edits visible without opening chat.
  const poll = usePoll(30_000, { only: refreshProps }, { autoStart: Boolean(props.project) })
  const [view, setView] = useState<ProjectManagementView>(() => {
    if (isSummary) return 'summary'
    const requested = query.get('view')
    if (requested && taskViews.includes(requested)) return requested as ProjectManagementView
    try {
      const saved = window.sessionStorage.getItem('fde:task-view:v2')
      if (saved && taskViews.includes(saved)) return saved as ProjectManagementView
    } catch {
      /* Browser storage can be unavailable in embedded contexts. */
    }
    return 'board'
  })
  const requestedView = query.get('view')
  useEffect(() => {
    if (isSummary) setView('summary')
    else if (requestedView && taskViews.includes(requestedView))
      setView(requestedView as ProjectManagementView)
  }, [isSummary, requestedView])
  function changeView(next: ProjectManagementView) {
    setView(next)
    try {
      window.sessionStorage.setItem('fde:task-view:v2', next)
    } catch {
      /* Keep navigation usable without storage. */
    }
    router.get(
      fdeProjectPath(fdeRoutes.tasks, props.project?.id, { view: next }),
      {},
      { preserveState: true, preserveScroll: true },
    )
  }
  const [monthOffset, setMonthOffset] = useState(0)
  const [groupBy, setGroupBy] = useState<'status' | 'category'>('status')
  const [showChildren, setShowChildren] = useState(false)
  const scopeRef = useRef(props.project?.id)
  useEffect(() => {
    if (scopeRef.current === props.project?.id) return
    scopeRef.current = props.project?.id
    setGroupBy('status')
    setShowChildren(false)
    setFilters(defaultFilters)
  }, [props.project?.id])
  const [filters, setFilters] = useState<ProjectTaskFilters>(() => ({
    ...defaultFilters,
    status: ['open', 'attention', 'done'].includes(query.get('status') || '')
      ? (query.get('status') as ProjectTaskFilters['status'])
      : 'all',
  }))
  const overdueIds = new Set(props.overdue_task_ids)
  const attentionIds = new Set(props.attention_task_ids)
  const assignees = useMemo(
    () =>
      Array.from(
        new Map(
          props.tasks.flatMap((task) => task.assignees).map((person) => [person.value, person]),
        ).values(),
      ).sort((a, b) => a.name.localeCompare(b.name)),
    [props.tasks],
  )
  const projectedTasks = projectTaskMoveProjection(props.tasks, actions.pendingMove)
  const tasks = projectedTasks.filter((task) => {
    const status = filters.status
    return (
      (view !== 'board' ||
        !task.parent_task_id ||
        showChildren ||
        filters.query.trim() ||
        filters.assignee ||
        filters.status !== 'all') &&
      (status === 'all' ||
        status === task.status ||
        (status === 'open' && task.status !== 'done') ||
        (status === 'attention' && attentionIds.has(task.id))) &&
      (!filters.assignee || task.assignees.some((person) => person.value === filters.assignee)) &&
      `${task.identifier} ${task.id} ${task.title} ${task.description ?? ''}`
        .toLocaleLowerCase()
        .includes(filters.query.trim().toLocaleLowerCase())
    )
  })
  if (!props.project)
    return (
      <div className="flex h-full items-center justify-center p-8 text-center">
        <p className="max-w-md text-sm text-muted-foreground">
          {props.copy.workspace.new_project_prompt}
        </p>
      </div>
    )
  const projectId = props.project.id
  const completionLabel = props.copy.management.completed_count
    .replace('%{completed}', String(props.metrics.completed_tasks ?? 0))
    .replace('%{total}', String(props.metrics.total_tasks ?? 0))

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <header className="flex shrink-0 items-center justify-between gap-4 border-b border-border px-4 py-5 lg:px-6">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold tracking-tight">
            {view === 'summary'
              ? props.copy.management.summary_project_title
              : props.copy.management.title}
          </h2>
          {!isSummary && (
            <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
              {props.project.objective || props.copy.management.work_caption}
            </p>
          )}
        </div>
        {(view !== 'summary' || actions.notice || actions.busyId) && (
          <div className="max-w-28 shrink-0 text-right sm:max-w-none">
            <p role="status" className="text-[11px] tabular-nums text-muted-foreground sm:text-xs">
              {actions.busyId
                ? props.copy.management.saving
                : actions.notice || (view !== 'summary' ? completionLabel : '')}
            </p>
          </div>
        )}
      </header>
      {!isSummary && (
        <ProjectManagementToolbar
          groupBy={groupBy}
          onGroup={setGroupBy}
          showChildren={showChildren}
          onChildren={setShowChildren}
          props={props}
          view={view}
          onView={changeView}
          filters={filters}
          onFilters={setFilters}
          assignees={assignees}
          attentionCount={attentionIds.size}
          onNew={() => router.visit(fdeProjectPath(`${fdeRoutes.projectTasks}/new`, projectId))}
        />
      )}
      <div className="min-h-0 min-w-0 flex-1 bg-muted/10">
        {view === 'summary' ? (
          <ProjectWorkSummary
            key={projectId}
            plan={props.project_plan}
            historyCopy={props.management_history_copy}
            objectives={props.objectives}
            objectiveCopy={props.objective_copy}
            fileDirectory={props.project_plan_files}
            today={props.management_today}
            summary={props.work_summary}
            tasks={props.tasks}
            projectId={projectId}
            canViewFiles={props.accessible_modules.includes('workspace')}
            overdueIds={overdueIds}
            canManage={props.can_manage}
            busy={Boolean(actions.busyId)}
            copy={props.copy}
            onSelect={(id) =>
              router.visit(fdeProjectPath(`${fdeRoutes.projectTasks}/${id}`, projectId))
            }
            onPatch={actions.patch}
            onCopy={actions.copyLink}
            onScope={(status) => {
              router.visit(fdeProjectPath(fdeRoutes.tasks, projectId, { view: 'list', status }))
            }}
          />
        ) : (
          <ProjectTaskCanvas
            key={projectId}
            allTasks={projectedTasks}
            filterKey={JSON.stringify(filters)}
            groupBy={groupBy}
            projectId={projectId}
            canViewEvents={props.accessible_modules.includes('timeline')}
            canViewFiles={props.accessible_modules.includes('workspace')}
            calendarQuery={filters.query}
            calendarPerson={assignees.find((person) => person.value === filters.assignee)}
            tasks={tasks}
            overdueIds={overdueIds}
            onSelect={(id) =>
              router.visit(fdeProjectPath(`${fdeRoutes.projectTasks}/${id}`, projectId))
            }
            canManage={props.can_manage}
            busy={Boolean(actions.busyId)}
            copy={props.copy}
            onPatch={actions.patch}
            onCopy={actions.copyLink}
            view={view}
            total={props.tasks.length}
            today={props.management_today}
            monthOffset={monthOffset}
            onMonth={setMonthOffset}
            onMove={actions.move}
            onReset={() => setFilters(defaultFilters)}
            onDragStart={poll.stop}
            onDragFinish={poll.start}
          />
        )}
      </div>
    </div>
  )
}
